import React, { useState, useRef, useEffect } from 'react'
import type { ReviewPayload, SourceProfile } from '@shared/types.js'

interface UploadProps {
    onCancel: () => void
    onReview: (payload: ReviewPayload) => void
}

export default function Upload({ onCancel, onReview }: UploadProps) {
    const [profiles, setProfiles] = useState<SourceProfile[]>([])
    const [selectedProfileId, setSelectedProfileId] = useState('')

    // File State
    const [file, setFile] = useState<File | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // URL State
    const [urlInput, setUrlInput] = useState('')

    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        fetch('/api/profiles')
            .then(res => res.json())
            .then((data: any) => {
                setProfiles(data.profiles || [])
                if (data.profiles && data.profiles.length > 0) {
                    setSelectedProfileId(data.profiles[0].id)
                }
            })
            .catch(err => console.error('Failed to load profiles', err))
    }, [])

    const handleFileDrop = (e: React.DragEvent) => {
        e.preventDefault()
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const dropped = e.dataTransfer.files[0]
            if (dropped.name.toLowerCase().endsWith('.eml')) {
                setFile(dropped)
                setUrlInput('')
                setError(null)
            } else {
                setError('Only .eml files are supported')
            }
        }
    }

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0])
            setUrlInput('')
            setError(null)
        }
    }

    const handleUpload = async () => {
        if (!selectedProfileId) return

        const isUrl = !!(urlInput && urlInput.trim())
        const isEml = !!file

        if (!isUrl && !isEml) return

        if (isUrl && !urlInput.trim().startsWith('http')) {
            setError('Please enter a valid HTTP or HTTPS URL')
            return
        }

        setUploading(true)
        setError(null)

        const formData = new FormData()
        formData.append('profileId', selectedProfileId)

        if (isUrl) {
            formData.append('url', urlInput.trim())
        } else if (isEml) {
            formData.append('file', file!)
        }

        try {
            const res = await fetch('/api/ingest', {
                method: 'POST',
                body: formData,
            })

            const data = await res.json() as any

            if (!res.ok) {
                let msg = data.error || 'Upload failed'
                if (data.details) msg += `: ${data.details}`
                throw new Error(msg)
            }

            // Success! Move to review screen
            onReview(data as ReviewPayload)

        } catch (e) {
            setError(e instanceof Error ? e.message : String(e))
        } finally {
            setUploading(false)
            // Note: intentionally bypassing `setUrlInput('')` here so on failure users don't lose typed text
        }
    }

    // Determine readiness
    const isReady = !!file || !!(urlInput && urlInput.trim())

    return (
        <div className="card animate-fade-in" style={{ maxWidth: '600px', margin: '0 auto' }}>
            <h2 className="text-center mb-lg">New Episode</h2>

            {error && (
                <div className="badge badge-error" style={{ display: 'block', padding: '12px', marginBottom: '20px', borderRadius: '8px' }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            <div className="form-group">
                <label htmlFor="profile">Source Profile</label>
                <select
                    id="profile"
                    value={selectedProfileId}
                    onChange={(e) => setSelectedProfileId(e.target.value)}
                    disabled={uploading || profiles.length === 0}
                >
                    {profiles.length === 0 ? (
                        <option>Loading profiles...</option>
                    ) : (
                        profiles.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))
                    )}
                </select>
            </div>

            <div className="form-group animate-fade-in mt-lg">
                <label>Article or Blog Web Link</label>
                <input
                    type="url"
                    placeholder="https://example.com/article"
                    value={urlInput}
                    onChange={(e) => {
                        setUrlInput(e.target.value)
                        if (e.target.value) setFile(null)
                    }}
                    disabled={uploading}
                    style={{
                        width: '100%',
                        padding: '16px',
                        fontSize: '1rem',
                        borderRadius: '8px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'white',
                        outline: 'none'
                    }}
                />
                <p className="text-xs text-secondary mt-sm">
                    Enter a direct link to an article, dev blog, or news post.
                </p>
            </div>

            <div className="text-center text-sm text-secondary" style={{ margin: '16px 0', fontWeight: 'bold' }}>OR</div>

            <div className="form-group animate-fade-in">
                <label>Newsletter File (.eml)</label>

                <div
                    className={`dropzone ${file ? 'active' : ''}`}
                    onDragOver={e => e.preventDefault()}
                    onDrop={handleFileDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{ opacity: urlInput ? 0.3 : 1, pointerEvents: urlInput ? 'none' : 'auto' }}
                >
                    <span className="dropzone-icon">📧</span>
                    {file ? (
                        <div>
                            <h4 style={{ color: 'var(--brand-secondary)' }}>{file.name}</h4>
                            <p className="text-sm mt-sm">{(file.size / 1024).toFixed(1)} KB</p>
                        </div>
                    ) : (
                        <div>
                            <h4>Click or drag .eml file here</h4>
                            <p className="text-sm mt-sm">Must be a raw email file containing the newsletter body.</p>
                        </div>
                    )}
                </div>
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".eml"
                    onChange={handleFileSelect}
                />
            </div>

            <div className="flex justify-between items-center mt-xl border-t" style={{ borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '24px' }}>
                <button className="btn btn-secondary" onClick={onCancel} disabled={uploading}>
                    Cancel
                </button>

                <button
                    className="btn btn-primary"
                    onClick={handleUpload}
                    disabled={!isReady || !selectedProfileId || uploading}
                    style={{ width: '150px' }}
                >
                    {uploading ? 'Processing...' : 'Upload & Parse'}
                </button>
            </div>

            {uploading && (
                <div className="mt-md text-center text-sm text-secondary animate-fade-in">
                    <p>Extracting text and running AI classification...</p>
                    <div className="progress-bg mt-sm">
                        <div className="progress-bar progress-indeterminate"></div>
                    </div>
                </div>
            )}
        </div>
    )
}
